// SPDX-License-Identifier: AGPL-3.0-or-later

import {ChessPromotionPicker} from '@app/features/table_game/components/ChessPromotionPicker';
import TableGames from '@app/features/table_game/state/TableGames';
import {Button} from '@app/features/ui/button/Button';
import styles from '@app/features/voice_scene/components/VoiceSceneSeatHud.module.css';
import type {TableSceneHud} from '@app/features/voice_scene/hooks/useTableGameScene';
import {type RoomWalkProbe, useWalkHint} from '@app/features/voice_scene/hooks/useWalkHint';
import {useWalkPointerHint} from '@app/features/voice_scene/hooks/useWalkPointerHint';
import type {SceneCameraMode} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import RoomAvatarSignals from '@app/features/voice_scene/state/RoomAvatarSignals';
import {ROOM_LIMITS} from '@fluxer/constants/src/RoomCharacters';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type {Ref} from 'react';

const BACK_TO_ROOM_DESCRIPTOR = msg({
	message: 'Back to room',
	comment:
		'3D voice room, seated at a table: leave the close-up table view and look at the whole room. You keep your seat.',
});
const TABLE_PANEL_DESCRIPTOR = msg({
	message: 'Table panel',
	comment:
		'3D voice room, seated at a table: open the flat table panel (the same game as buttons and lists; for keyboard and screen readers).',
});
const OPEN_STAGED_DESCRIPTOR = msg({
	message: 'Open ({count})',
	comment:
		'3D voice room, 101 Okey: lay down the sets placed on the table so far; {count} is how many tiles they hold.',
});
const ADD_STAGED_DESCRIPTOR = msg({
	message: 'Add ({count})',
	comment: 'Table games: 101 Okey, send the tiles placed onto sets by Auto add; {count} is how many tiles.',
});
const AUTO_ADD_DESCRIPTOR = msg({
	message: 'Auto add',
	comment:
		'3D voice room, 101 Okey: place every rack tile that fits a set on the table onto that set in one tap (confirmed with Open, dropped with Cancel).',
});
const CANCEL_DESCRIPTOR = msg({
	message: 'Cancel',
	context: 'voice-scene-seat-hud',
	comment: '3D voice room, at a table: drop the pending sets or the path choice and put the pieces back.',
});
const UNDO_STEP_DESCRIPTOR = msg({
	message: 'Undo',
	context: 'voice-scene-seat-hud',
	comment: '3D voice room, tavla: take back the last checker move of this turn (not sent yet).',
});
const DONE_TURN_DESCRIPTOR = msg({
	message: 'Done',
	context: 'voice-scene-seat-hud',
	comment: '3D voice room, tavla: send the moves of this turn.',
});
const CHOOSE_PATH_DESCRIPTOR = msg({
	message: 'Which way?',
	comment: '3D voice room, dama: several capture paths lead to the same square; pick the one to play.',
});

const STAND_UP_HINT_DESCRIPTOR = msg({
	message: 'Stand up',
	context: 'voice-scene-walk',
	comment:
		'3D voice room, seated at a table: shown next to the E key; pressing E gets up from the table (a running game asks first, since leaving it loses it).',
});
const SIT_HINT_DESCRIPTOR = msg({
	message: 'Sit',
	context: 'voice-scene-walk',
	comment: '3D voice room, walking: shown next to the E key when a free seat is close; pressing E sits down there.',
});
const CROWDED_HINT_DESCRIPTOR = msg({
	message: 'Walking is off in a crowded room',
	comment: '3D voice room: shown while the room has too many people for walking around; everyone stays seated.',
});
const RELEASE_CURSOR_HINT_DESCRIPTOR = msg({
	message: 'Alt, Esc or middle click: cursor',
	comment:
		'3D voice room, mouse look: briefly explains the keys that release the cursor while walking. Middle click is a press of the mouse wheel.',
});
const KNOCKED_OUT_HINT_DESCRIPTOR = msg({
	message: 'You are knocked out',
	comment:
		'3D voice room: shown while your character lies knocked out by slaps (about 15 seconds); it cannot walk or move until it gets up on its own.',
});
const CLICK_TO_LOOK_HINT_DESCRIPTOR = msg({
	message: 'Click or middle click to look',
	comment:
		'3D voice room, walking with the cursor released: click the room, or press the mouse wheel, to resume mouse look.',
});

// The walking hint over the room, bottom centre: "E Sit" next to a free seat, or why walking is off (crowded, or my
// character lies knocked out). A polite status region, so a screen reader hears it once as it appears. Nothing while
// walking is off in this room (walk null).
export const VoiceSceneWalkHint = observer(function VoiceSceneWalkHint({
	walk,
	participantCount,
	walking = true,
	mode = 'orbit',
	container = null,
}: {
	walk: RoomWalkProbe | null;
	participantCount: number;
	// My character walks now (the sit hint looks for a seat only then).
	walking?: boolean;
	mode?: SceneCameraMode;
	container?: HTMLElement | null;
}) {
	const {i18n} = useLingui();
	const hint = useWalkHint(walk, participantCount, walking);
	const pointerHint = useWalkPointerHint(
		container,
		walk !== null &&
			walking &&
			participantCount <= ROOM_LIMITS.posMaxParticipants &&
			(mode === 'eyes' || mode === 'follow'),
	);
	if (!walk) return null;
	return (
		<div className={styles.walkHint} role="status" aria-live="polite" data-flx="voice-scene.voice-scene-walk-hint">
			{RoomAvatarSignals.knockedOut ? (
				<span>{i18n._(KNOCKED_OUT_HINT_DESCRIPTOR)}</span>
			) : hint === 'sit' ? (
				<>
					<kbd className={styles.key}>E</kbd>
					<span>{i18n._(SIT_HINT_DESCRIPTOR)}</span>
				</>
			) : hint === 'crowded' ? (
				<span>{i18n._(CROWDED_HINT_DESCRIPTOR)}</span>
			) : null}
			{pointerHint ? (
				<span>{i18n._(pointerHint === 'locked' ? RELEASE_CURSOR_HINT_DESCRIPTOR : CLICK_TO_LOOK_HINT_DESCRIPTOR)}</span>
			) : null}
		</div>
	);
});

// The seat view's controls, on the right edge so my rack (bottom centre) and the room toolbar (bottom left) stay
// clear. Watchers get only the way out and the panel; the table itself is driven by dragging. "E Stand up" shows where
// E gets up from the seat (walking on in this room).
export function VoiceSceneSeatHud({
	hud,
	channelId,
	slot,
	onExit,
	standKey = false,
	ref,
}: {
	// null while the table has no view yet (a free table, or before the first read).
	hud: TableSceneHud | null;
	channelId: string;
	slot: string;
	onExit: () => void;
	standKey?: boolean;
	// The table panel opens beside the HUD.
	ref?: Ref<HTMLDivElement>;
}) {
	const {i18n} = useLingui();
	const playing = hud?.role === 'player' && hud.interactive ? hud : null;
	return (
		<div ref={ref} className={styles.hud} data-seat-hud="" data-flx="voice-scene.voice-scene-seat-hud.hud">
			<Button
				small
				compact
				fitContainer
				variant="inverted"
				onClick={onExit}
				data-flx="voice-scene.voice-scene-seat-hud.button.exit"
			>
				{i18n._(BACK_TO_ROOM_DESCRIPTOR)}
			</Button>
			<Button
				small
				compact
				fitContainer
				variant="inverted-outline"
				onClick={() => TableGames.requestPanel(channelId, slot)}
				data-flx="voice-scene.voice-scene-seat-hud.button.request-panel"
			>
				{i18n._(TABLE_PANEL_DESCRIPTOR)}
			</Button>
			{standKey ? (
				<div className={styles.keyHint} data-flx="voice-scene.voice-scene-seat-hud.stand-hint">
					<kbd className={styles.key}>E</kbd>
					<span>{i18n._(STAND_UP_HINT_DESCRIPTOR)}</span>
				</div>
			) : null}
			{playing?.game === 'okey101' && (playing.stage || playing.canAutoAdd) ? (
				<div className={styles.group} data-flx="voice-scene.voice-scene-seat-hud.group">
					{playing.stage ? (
						<Button
							small
							compact
							fitContainer
							variant="inverted"
							onClick={playing.openStage}
							data-flx="voice-scene.voice-scene-seat-hud.button.open-stage"
						>
							{i18n._(playing.stage.open.length > 0 ? OPEN_STAGED_DESCRIPTOR : ADD_STAGED_DESCRIPTOR, {
								count: playing.stagedCount,
							})}
						</Button>
					) : null}
					{playing.canAutoAdd ? (
						<Button
							small
							compact
							fitContainer
							variant={playing.stage ? 'inverted-outline' : 'inverted'}
							onClick={playing.autoAdd}
							data-flx="voice-scene.voice-scene-seat-hud.button.auto-add"
						>
							{i18n._(AUTO_ADD_DESCRIPTOR)}
						</Button>
					) : null}
					{playing.stage ? (
						<Button
							small
							compact
							fitContainer
							variant="inverted-outline"
							onClick={playing.cancelStage}
							data-flx="voice-scene.voice-scene-seat-hud.button.cancel-stage"
						>
							{i18n._(CANCEL_DESCRIPTOR)}
						</Button>
					) : null}
				</div>
			) : null}
			{playing?.game === 'tavla' && playing.tavlaSteps.length > 0 ? (
				<div className={styles.group} data-flx="voice-scene.voice-scene-seat-hud.group--2">
					<Button
						small
						compact
						fitContainer
						variant="inverted-outline"
						onClick={playing.undoTavla}
						data-flx="voice-scene.voice-scene-seat-hud.button.undo-tavla"
					>
						{i18n._(UNDO_STEP_DESCRIPTOR)}
					</Button>
					<Button
						small
						compact
						fitContainer
						variant="inverted"
						disabled={!playing.tavlaComplete}
						onClick={playing.confirmTavla}
						data-flx="voice-scene.voice-scene-seat-hud.button.confirm-tavla"
					>
						{i18n._(DONE_TURN_DESCRIPTOR)}
					</Button>
				</div>
			) : null}
			{playing?.pathChoice ? (
				<div
					className={styles.group}
					role="group"
					aria-label={i18n._(CHOOSE_PATH_DESCRIPTOR)}
					data-flx="voice-scene.voice-scene-seat-hud.group--3"
				>
					<span className={styles.caption} data-flx="voice-scene.voice-scene-seat-hud.caption">
						{i18n._(CHOOSE_PATH_DESCRIPTOR)}
					</span>
					{playing.pathChoice.map((path) => (
						<Button
							key={path.join('-')}
							small
							compact
							fitContainer
							variant="inverted-outline"
							onClick={() => playing.choosePath(path)}
							onMouseEnter={() => playing.previewPath(path)}
							onMouseLeave={() => playing.previewPath(null)}
							onFocus={() => playing.previewPath(path)}
							onBlur={() => playing.previewPath(null)}
							data-flx="voice-scene.voice-scene-seat-hud.button.choose-path"
						>
							{path.join(' → ')}
						</Button>
					))}
					<Button
						small
						compact
						fitContainer
						variant="inverted-outline"
						onClick={playing.dismissChoice}
						data-flx="voice-scene.voice-scene-seat-hud.button.dismiss-choice"
					>
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
				</div>
			) : null}
			{playing?.promotion ? (
				<ChessPromotionPicker
					side={playing.promotion.side}
					onPick={playing.pickPromotion}
					data-flx="voice-scene.voice-scene-seat-hud.chess-promotion-picker"
				/>
			) : null}
		</div>
	);
}
